import React, { useState, useCallback, useRef, useEffect } from 'react';
import api, { API_URL } from '../api';
import KaraokePlayer from '../components/KaraokePlayer';
import VideoPlayer from '../components/VideoPlayer';

interface Song {
  id: string; title: string; artist: string;
  duration_seconds: number; genre: string;
  has_cdg: boolean; has_lrc: boolean; has_video: boolean; is_favorite: boolean;
}

export default function Catalog() {
  const [songs, setSongs] = useState<Song[]>([]);
  const [total, setTotal] = useState(0);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [playing, setPlaying] = useState<Song | null>(null);
  const [streamToken, setStreamToken] = useState<string | null>(null);
  const [isVideo, setIsVideo] = useState(false);
  const [favorites, setFavorites] = useState<Song[]>([]);
  const [activeTab, setActiveTab] = useState<'search' | 'favorites'>('search');
  const [noSubMsg, setNoSubMsg] = useState(false);
  const [searched, setSearched] = useState(false);
  const searchRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tenant = JSON.parse(localStorage.getItem('customer_tenant') || '{}');
  const color = tenant.color || '#7C3AED';
  const user = JSON.parse(localStorage.getItem('customer_user') || '{}');

  // Cargar total del catálogo al inicio
  useEffect(() => {
    api.get('/api/customer/songs?q= &limit=1').then(({ data }) => {
      if (data.catalogTotal) setCatalogTotal(data.catalogTotal);
    }).catch(() => {});
  }, []);

  const fetchSongs = useCallback(async (q: string) => {
    if (!q.trim()) { setSongs([]); setTotal(0); setSearched(false); return; }
    setLoading(true);
    setSearched(true);
    try {
      const { data } = await api.get(`/api/customer/songs?q=${encodeURIComponent(q)}&limit=5000`);
      setSongs(data.songs);
      setTotal(data.total || data.songs.length);
      if (data.catalogTotal) setCatalogTotal(data.catalogTotal);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchFavorites = useCallback(async () => {
    try {
      const { data } = await api.get('/api/customer/songs/favorites');
      setFavorites(data.favorites);
    } catch (err) { console.error(err); }
  }, []);

  const handleSearchChange = (q: string) => {
    setSearch(q);
    if (searchRef.current) clearTimeout(searchRef.current);
    searchRef.current = setTimeout(() => fetchSongs(q), 400);
  };

  const handlePlay = async (song: Song) => {
    setNoSubMsg(false);
    try {
      const { data } = await api.post(`/songs/${song.id}/token`);
      setStreamToken(data.token);
      setIsVideo(data.has_video || false);
      setPlaying(song);
    } catch (err: any) {
      if (err.response?.status === 403) setNoSubMsg(true);
    }
  };

  const handleFavorite = async (song: Song, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.post(`/api/customer/songs/${song.id}/favorite`);
      setSongs(prev => prev.map(s => s.id === song.id ? { ...s, is_favorite: !s.is_favorite } : s));
      fetchFavorites();
    } catch (err) { console.error(err); }
  };

  const formatDur = (s: number) => s ? `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}` : '';

  if (playing && streamToken) {
    if (isVideo) {
      return <VideoPlayer
        song={playing} streamToken={streamToken} apiUrl={API_URL}
        userId={user.id} token={localStorage.getItem('customer_token') || ''}
        onFinished={() => { setPlaying(null); setStreamToken(null); setIsVideo(false); }}
      />;
    }
    return <KaraokePlayer
      song={playing} streamToken={streamToken} apiUrl={API_URL}
      onFinished={() => { setPlaying(null); setStreamToken(null); }}
    />;
  }

  return (
    <div style={{ minHeight:'100vh', background:'#0f0f1a', color:'#fff', fontFamily:'sans-serif' }}>
      <div style={{ padding:'1rem 1.5rem 0.75rem', background:'#1a1a2e', borderBottom:'1px solid #2a2a3e', position:'sticky', top:0, zIndex:10 }}>
        <div style={{ display:'flex', gap:'0.5rem', marginBottom:'0.75rem' }}>
          {(['search','favorites'] as const).map(t => (
            <button key={t} onClick={() => { setActiveTab(t); if (t === 'favorites') fetchFavorites(); }}
              style={{ padding:'0.4rem 1rem', background: activeTab === t ? color : '#2a2a3e', border:'none', borderRadius:'99px', color:'#fff', cursor:'pointer', fontSize:'0.85rem' }}>
              {t === 'search' ? '🔍 Buscar' : `❤️ Favoritas (${favorites.length})`}
            </button>
          ))}
        </div>
        {activeTab === 'search' && (
          <input
            style={{ width:'100%', padding:'0.85rem 1rem', background:'#0f0f1a', border:`2px solid ${color}66`, borderRadius:'12px', color:'#fff', fontSize:'1rem', boxSizing:'border-box' as const, outline:'none' }}
            placeholder="🎵 Busca por título o artista..."
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
          />
        )}
      </div>

      {noSubMsg && (
        <div style={{ background:'#7f1d1d', padding:'0.75rem 1.5rem', display:'flex', justifyContent:'space-between' }}>
          <span style={{ fontSize:'0.9rem' }}>🔒 Necesitas suscripción para reproducir</span>
          <button onClick={() => setNoSubMsg(false)} style={{ background:'none', border:'none', color:'#fff', cursor:'pointer' }}>✕</button>
        </div>
      )}

      <div style={{ padding:'0.75rem 1rem' }}>
        {activeTab === 'search' && (
          <>
            {!searched && (
              <div style={{ textAlign:'center', padding:'4rem 2rem', color:'#555' }}>
                <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>🎤</div>
                <div style={{ fontSize:'1.1rem', color:'#888' }}>Busca tu canción favorita</div>
                <div style={{ fontSize:'1.5rem', fontWeight:900, color:color, marginTop:'0.75rem' }}>
                  {catalogTotal > 0 ? catalogTotal.toLocaleString() : '...'} canciones disponibles
                </div>
              </div>
            )}
            {searched && loading && <p style={{ textAlign:'center', color:'#888', padding:'2rem' }}>Buscando...</p>}
            {searched && !loading && songs.length === 0 && (
              <p style={{ textAlign:'center', color:'#888', padding:'2rem' }}>No se encontraron canciones para "{search}"</p>
            )}
            {searched && !loading && songs.length > 0 && (
              <>
                <p style={{ color:'#555', fontSize:'0.82rem', marginBottom:'0.75rem', padding:'0 0.25rem' }}>
                  {total.toLocaleString()} resultado{total !== 1 ? 's' : ''}
                </p>
                {songs.map(song => (
                  <SongRow key={song.id} song={song} color={color} onPlay={handlePlay} onFavorite={handleFavorite} formatDur={formatDur} />
                ))}
              </>
            )}
          </>
        )}

        {activeTab === 'favorites' && (
          favorites.length === 0 ? (
            <div style={{ textAlign:'center', padding:'4rem', color:'#555' }}>
              <div style={{ fontSize:'2rem' }}>❤️</div>
              <div>Aún no tienes favoritas</div>
            </div>
          ) : favorites.map(song => (
            <SongRow key={song.id} song={song} color={color} onPlay={handlePlay} onFavorite={handleFavorite} formatDur={formatDur} />
          ))
        )}
      </div>
    </div>
  );
}

function SongRow({ song, color, onPlay, onFavorite, formatDur }: any) {
  return (
    <div onClick={() => onPlay(song)} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.85rem 1rem', marginBottom:'0.35rem', background:'#1a1a2e', borderRadius:'10px', cursor:'pointer', border:'1px solid #2a2a3e' }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:'0.95rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{song.title}</div>
        <div style={{ color:'#888', fontSize:'0.82rem', marginTop:'0.15rem' }}>{song.artist}</div>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0 }}>
        {song.has_video && <span style={{ fontSize:'0.65rem', background:'#1e3a5f', padding:'2px 6px', borderRadius:'99px', color:'#93c5fd' }}>📹</span>}
        {(song.has_cdg || song.has_lrc) && <span style={{ fontSize:'0.65rem', background:'#4c1d95', padding:'2px 6px', borderRadius:'99px', color:'#c4b5fd' }}>🎵</span>}
        <span style={{ color:'#555', fontSize:'0.75rem', fontFamily:'monospace' }}>{formatDur(song.duration_seconds)}</span>
        <button onClick={(e) => onFavorite(song, e)} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'1.1rem', padding:'2px' }}>
          {song.is_favorite ? '❤️' : '🤍'}
        </button>
        <span style={{ color, fontSize:'1.2rem' }}>▶</span>
      </div>
    </div>
  );
}
